import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Loader from "components/Loader";
import { getSurveyorGoals } from "../../../../../requesters/surveyorGoalsRequester";
import { getBaseUrl } from "../../../reducers";
import DashboardCardContainer from "./DashboardCardContainer";
import GoalMetricCard from "./GoalMetricCard";

export default function GoalSummarySection({ currentUser, surveyorId, inline }) {
  const baseUrl = useSelector(getBaseUrl);
  const [goals, setGoals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userId = surveyorId ?? currentUser?.id;

  useEffect(() => {
    if (userId) {
      setLoading(true);
      setError(null);

      getSurveyorGoals(baseUrl, userId)
        .then(res => {
          if (res.status === 200) {
            setGoals(res.response?.data?.attributes);
          } else if (res.error) {
            setError("Failed to load goal data");
          }
        })
        .catch(err => {
          console.error("Error fetching surveyor goals:", err);
          setError("Failed to load goal data");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setGoals(null);
      setLoading(false);
      setError(null);
    }
  }, [userId, baseUrl]);

  const formatHoursAccrual = value => {
    if (value === null || value === undefined) return "—";
    const numValue = parseFloat(value);
    if (numValue >= 0) {
      return `+${numValue.toFixed(2)}`;
    }
    return numValue.toFixed(2);
  };

  const formatPercentage = value => {
    if (value === null || value === undefined) return "—";
    return `${parseFloat(value).toFixed(2)}%`;
  };

  const formatHoursRatio = weeklyHours => {
    if (!weeklyHours) return "—";
    const actual = weeklyHours.actual || 0;
    const target = weeklyHours.target || 0;
    return `${actual.toFixed(2)} / ${target.toFixed(2)}`;
  };

  const formatJobsPerDay = jobsData => {
    if (!jobsData || jobsData.average === null || jobsData.average === undefined) return "—";
    return jobsData.average.toFixed(2);
  };

  const getHoursAccrualColorClass = value => {
    if (value === null || value === undefined) return "bg-light";
    return parseFloat(value) >= 0 ? "bg-light" : "bg-coral";
  };

  const retryFetch = () => {
    if (!userId) return;
    setError(null);
    setLoading(true);
    getSurveyorGoals(baseUrl, userId)
      .then(res => {
        if (res.status === 200) {
          setGoals(res.response?.data?.attributes);
        } else if (res.error) {
          setError("Failed to load goal data");
        }
      })
      .catch(err => {
        console.error("Error fetching surveyor goals:", err);
        setError("Failed to load goal data");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const scrollToGoalSection = () => {
    if (window.location.hash !== "#goal-summary") return;
    const element = document.getElementById("goal-summary");
    if (element) {
      setTimeout(() => element.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  };

  useEffect(() => {
    scrollToGoalSection();
  }, [loading, goals, error]);

  const contentWrapper = (content) => {
    if (inline) {
      return <div className="row" id="goal-summary">{content}</div>;
    }
    return (
      <div className="row no-gutters mt-4 justify-content-end" id="goal-summary">
        <DashboardCardContainer title="GOAL SUMMARY">
          <div className="row">{content}</div>
        </DashboardCardContainer>
      </div>
    );
  };

  if (loading) {
    return contentWrapper(
      <div className="col-12 text-center py-4">
        <Loader />
      </div>
    );
  }

  if (error) {
    return contentWrapper(
      <div className="col-12 text-center py-4">
        <p className="text-danger">{error}</p>
        <button type="button" className="btn btn-primary btn-sm" onClick={retryFetch}>
          Retry
        </button>
      </div>
    );
  }

  if (!goals) {
    return null;
  }

  const isScanner = goals.user_type === "scanner";

  return contentWrapper(
    <>
      <GoalMetricCard
        title="Weekly Hours (Field)"
        value={formatHoursRatio(goals.weekly_hours)}
        colorClass="bg-light" />
      <GoalMetricCard
        title="Weekly Bonus Goal"
        value={formatPercentage(goals.weekly_dollars?.percentage)}
        colorClass="bg-light" />
      <GoalMetricCard
        title="Hours Owing (-) / Owed (+)"
        value={formatHoursAccrual(goals.hours_accrual)}
        colorClass={getHoursAccrualColorClass(goals.hours_accrual)} />
      <GoalMetricCard
        title="$ Bonus Goal (Overall)"
        value={formatPercentage(goals.dollars_accrual_percentage)}
        colorClass="bg-light" />
      {isScanner && (
        <>
          <GoalMetricCard
            title="Weekly Jobs Per Day"
            value={formatJobsPerDay(goals.weekly_jobs_per_day)}
            colorClass="bg-light" />
          <GoalMetricCard
            title="Jobs Per Day (Overall)"
            value={formatJobsPerDay(goals.jobs_per_day_accrual)}
            colorClass="bg-light" />
        </>
      )}
    </>
  );
}
